// components/PurchasePopup.jsx
import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { IndianRupee } from './CustomIcons';
import { imageUrl as imgUrl, apiUrls } from '../utils/url';
import { postRequest } from '../utils/networking';

// Helper functions
function getPath(fullPath) {
  if (!fullPath) return null;
  const normalized = fullPath.replace(/\\/g, "/");
  const assetsIndex = normalized.indexOf("assets/");
  if (assetsIndex !== -1) {
    return normalized.substring(assetsIndex + 7);
  }
  return normalized.split('/').pop();
}

const getProcessedImageUrl = (imageUrlSource) => {
  const FALLBACK_IMAGE = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="300" height="200" viewBox="0 0 300 200"%3E%3Crect width="300" height="200" fill="%23f3f4f6"/%3E%3Ctext x="50%25" y="50%25" font-size="14" text-anchor="middle" dy=".3em" fill="%239ca3af"%3ENo Image%3C/text%3E%3C/svg%3E';

  if (!imageUrlSource) return FALLBACK_IMAGE;
  if (imageUrlSource.startsWith('http')) return imageUrlSource;
  const extractedPath = getPath(imageUrlSource);
  if (!extractedPath) return FALLBACK_IMAGE;
  const cleanPath = extractedPath.replace(/^\/+/, '');
  return `${imgUrl}/${cleanPath}`;
};

const formatPrice = (price) => {
  let numericPrice;
  if (typeof price === 'string') {
    numericPrice = parseFloat(price.replace(/[^0-9.-]/g, ''));
  } else if (typeof price === 'number') {
    numericPrice = price;
  } else {
    numericPrice = 0;
  }
  return isNaN(numericPrice) ? '0.00' : numericPrice.toFixed(2);
};

const PurchasePopup = ({ course, onClose, onSubmit, isSubmitting = false }) => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [error, setError] = useState('');
  const [processingPayment, setProcessingPayment] = useState(false);

  useEffect(() => {
    const script = document.createElement('script');
    script.src = "https://sdk.phonepe.com/ppcheckout/sdk.js";
    script.async = true;
    document.body.appendChild(script);
    return () => { document.body.removeChild(script); };
  }, []);

  const handlePhonePePayment = async () => {
    try {
      setProcessingPayment(true);

      const amountInRupees = parseFloat(formatPrice(course?.price || 0));
      const redirectUrl = `${window.location.origin}/payment-success`;

      // Step 1: Create order in backend
      const payloadObj = {
        amount: amountInRupees,
        courseid: course?.courseid || course?.id,
        redirectUrl: redirectUrl,
        phone: phoneNumber
      };

      // Try to get email if available
      try {
        const userStr = localStorage.getItem("user");
        if (userStr) {
          const userObj = JSON.parse(userStr);
          if (userObj && (userObj.email || userObj.useremail)) {
            payloadObj.email = userObj.email || userObj.useremail;
          }
        }
      } catch (e) {}

      const orderResponse = await postRequest(apiUrls.coursemakeorder, payloadObj);
      console.log('order response', orderResponse);

      if (orderResponse?.status === "S" || orderResponse?.status === "s") {
        if (orderResponse.paymentUrl) {
          // Store details for verification page fallback
          localStorage.setItem("pending_payment_type", "course");
          localStorage.setItem("pending_course_id", course?.courseid || course?.id);
          localStorage.setItem("pending_phone", phoneNumber);
          let studentEmail = "";
          try {
            const userStr = localStorage.getItem("user");
            if (userStr) {
              const userObj = JSON.parse(userStr);
              if (userObj && (userObj.email || userObj.useremail)) {
                studentEmail = userObj.email || userObj.useremail;
              }
            }
          } catch (e) {
            console.error("Error parsing user from localStorage:", e);
          }
          if (studentEmail) {
            localStorage.setItem("pending_email", studentEmail);
          }

          // Use PhonePe Checkout SDK if available, otherwise fallback to redirect
          if (window.PhonePeCheckout && typeof window.PhonePeCheckout.open === 'function') {
            window.PhonePeCheckout.open({
              url: orderResponse.paymentUrl,
              onSuccess: async (result) => {
                // Result should contain transactionId
                const transactionId = result?.transactionId || result?.merchantTransactionId;
                if (!transactionId) {
                  console.error('Transaction ID missing in PhonePe success callback');
                  setError('Payment succeeded but could not retrieve transaction ID.');
                  setProcessingPayment(false);
                  return;
                }
                // Build payload similar to PaymentSuccess
                const payload = { transactionId };
                const courseId = localStorage.getItem('pending_course_id');
                let email = localStorage.getItem('pending_email');
                const phone = localStorage.getItem('pending_phone');
                if (!email) {
                  const userStr = localStorage.getItem('user');
                  if (userStr) {
                    try { const userObj = JSON.parse(userStr); email = userObj.email || userObj.useremail; } catch (_) {}
                  }
                }
                payload.courseid = courseId ? parseInt(courseId) : null;
                if (email) payload.email = email;
                if (phone) payload.phone = phone;
                try {
                  const verifyRes = await postRequest(apiUrls.verifycoursepayment, payload);
                  if (verifyRes?.status === 'S' || verifyRes?.status === 's') {
                    // success - navigate to WhatsApp group
                    // clear pending data
                    localStorage.removeItem('pending_payment_type');
                    localStorage.removeItem('pending_course_id');
                    localStorage.removeItem('pending_phone');
                    localStorage.removeItem('pending_email');
                    window.location.href = 'https://chat.whatsapp.com/GPymhKiAK4MCNei0iSDnCw';
                  } else {
                    setError(verifyRes?.message || 'Verification failed after payment.');
                  }
                } catch (verErr) {
                  console.error('Verification error after PhonePe success:', verErr);
                  setError('Error verifying payment after success.');
                }
                setProcessingPayment(false);
              },
              onFailure: (err) => {
                console.error('PhonePe payment failed:', err);
                setError(err?.message || 'PhonePe payment failed.');
                setProcessingPayment(false);
              }
            });
          } else {
            // Fallback: redirect to PhonePe page
            window.location.href = orderResponse.paymentUrl;
          }
        } else {
          throw new Error('Payment URL not returned from server');
        }
      } else {
        throw new Error(orderResponse?.message || 'Failed to create order. Please try again.');
      }
    } catch (err) {
      console.error('PhonePe error:', err);
      setError(err.message || 'Something went wrong with payment processing.');
      setProcessingPayment(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const phoneRegex = /^[6-9]\d{9}$/;
    if (!phoneNumber.trim()) {
      setError('Please enter your phone number');
      return;
    }
    if (!phoneRegex.test(phoneNumber)) {
      setError('Please enter a valid 10-digit mobile number');
      return;
    }

    setError('');
    await handlePhonePePayment();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-auto overflow-hidden">
        <div className="px-5 pt-4 pb-2 border-b border-slate-200 flex justify-between items-center">
          <h3 className="text-black font-semibold text-lg">Complete Purchase</h3>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 transition-colors"
            disabled={isSubmitting || processingPayment}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 pt-4 pb-2">
          <div className="flex gap-3">
            <div className="w-16 h-16 rounded-lg bg-slate-100 overflow-hidden flex-shrink-0">
              {course?.imgurl && (
                <img
                  src={getProcessedImageUrl(course.imgurl)}
                  alt={course.coursename}
                  className="w-full h-full object-cover"
                  onError={(e) => { e.target.src = 'https://via.placeholder.com/64?text=No+Image'; }}
                />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="font-semibold text-slate-800 text-sm line-clamp-2">
                {course?.coursename || 'Untitled Course'}
              </h4>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-xs text-slate-500">{course?.levelname || 'Beginner'}</span>
                <span className="text-xs text-orange-500 font-semibold flex items-center">
                  <IndianRupee className="w-3 h-3" />
                  {formatPrice(course?.price || 0)}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="border-b border-slate-100"></div>

        <form onSubmit={handleSubmit} className="p-5">
          <label className="block text-slate-700 text-sm font-medium mb-2">
            Phone Number <span className="text-red-500">*</span>
          </label>
          <input
            type="tel"
            value={phoneNumber}
            onChange={(e) => {
              setPhoneNumber(e.target.value.replace(/[^0-9]/g, '').slice(0, 10));
              setError('');
            }}
            placeholder="Enter 10-digit mobile number"
            className={`w-full px-4 py-3 text-sm border ${error ? 'border-red-400' : 'border-slate-200'} rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition text-slate-800 placeholder:text-slate-400`}
            disabled={isSubmitting || processingPayment}
          />
          {error && (
            <p className="mt-2 text-sm text-red-500">{error}</p>
          )}
          <button
            type="submit"
            disabled={isSubmitting || processingPayment}
            className="w-full mt-6 bg-gradient-to-r from-orange-500 to-amber-500 text-white font-bold py-3 px-6 rounded-xl transition-all duration-300 transform hover:scale-[1.02] disabled:opacity-70 disabled:cursor-not-allowed text-center flex items-center justify-center gap-2"
          >
            {(isSubmitting || processingPayment) ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                {processingPayment ? 'Opening Payment...' : 'Processing...'}
              </>
            ) : (
              <>
                Pay
                <span className="inline-flex items-center gap-0">
                  <IndianRupee className="w-4 h-4" />
                  {formatPrice(course?.price || 0)}
                </span>
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default PurchasePopup;